import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import en from './en.json';
import tr from './tr.json';

const DICTIONARIES = { en, tr };
const SUPPORTED = ['en', 'tr'];
const STORAGE_KEY = 'vitex.lang';

const detectInitialLang = () => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (SUPPORTED.includes(saved)) return saved;
  } catch {
    /* localStorage may be blocked */
  }
  const browser = (navigator.language || 'tr').toLowerCase();
  if (browser.startsWith('en')) return 'en';
  return 'tr';
};

const I18nContext = createContext({
  lang: 'en',
  setLang: () => {},
  t: (key) => key,
});

export function I18nProvider({ children }) {
  const [lang, setLangState] = useState(detectInitialLang);

  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);

  const setLang = useCallback((next) => {
    if (!SUPPORTED.includes(next)) return;
    setLangState(next);
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      /* ignore */
    }
  }, []);

  const messages = DICTIONARIES[lang] || DICTIONARIES.en;

  const t = useCallback(
    (key, vars) => {
      const fromActive = key.split('.').reduce((acc, k) => (acc && typeof acc === 'object' ? acc[k] : undefined), messages);
      const fromFallback = fromActive === undefined
        ? key.split('.').reduce((acc, k) => (acc && typeof acc === 'object' ? acc[k] : undefined), DICTIONARIES.en)
        : fromActive;
      const value = fromFallback === undefined ? key : fromFallback;
      if (vars && typeof value === 'string') {
        return Object.entries(vars).reduce(
          (acc, [k, v]) => acc.split(`{${k}}`).join(String(v)),
          value
        );
      }
      return value;
    },
    [messages]
  );

  const value = useMemo(() => ({ lang, setLang, t }), [lang, setLang, t]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n() {
  return useContext(I18nContext);
}

export const SUPPORTED_LANGS = SUPPORTED;
